import { supabase } from './supabase/client';
import type {
  AiChatMessage,
  AiChatResponse,
  AiChatError,
  AiChatErrorCode,
} from '@flows.cash/types';

// A turn can involve several tool rounds server-side.
const CLIENT_TIMEOUT_MS = 90_000;
const MAX_MESSAGE_CHARS = 2_000;
// Server clamps identically; keeping the client in sync avoids silent trims.
export const CHAT_HISTORY_LIMIT = 12;

export type AiChatResult =
  | { data: AiChatResponse; error: null }
  | { data: null; error: AiChatError };

function errorResponse(code: AiChatErrorCode): AiChatResult {
  return { data: null, error: { error: code } };
}

/**
 * Sends one chat turn to the money-chat Edge Function. History is client-held
 * (send the last few messages back each turn); the server keeps nothing.
 * Never throws — all failures resolve to an AiChatError.
 */
export async function sendChatMessage(
  message: string,
  history: AiChatMessage[],
  locale = 'en'
): Promise<AiChatResult> {
  const trimmed = message.trim();
  if (!trimmed) return errorResponse('chat_unavailable');
  if (trimmed.length > MAX_MESSAGE_CHARS) return errorResponse('message_too_long');

  const timeout = new Promise<AiChatResult>((resolve) =>
    setTimeout(() => resolve(errorResponse('chat_unavailable')), CLIENT_TIMEOUT_MS)
  );

  const request = (async (): Promise<AiChatResult> => {
    try {
      const { data, error } = await supabase.functions.invoke('money-chat', {
        body: {
          message: trimmed,
          history: history.slice(-CHAT_HISTORY_LIMIT),
          locale,
        },
      });

      if (error) {
        const status: number | undefined = (error as any)?.context?.status;
        if (status === 429) return errorResponse('rate_limited');
        if (status === 400) return errorResponse('message_too_long');
        return errorResponse('chat_unavailable');
      }

      if (typeof data?.reply !== 'string') {
        return errorResponse('chat_unavailable');
      }

      return { data: data as AiChatResponse, error: null };
    } catch (err) {
      console.error('sendChatMessage failed:', err);
      return errorResponse('chat_unavailable');
    }
  })();

  return Promise.race([request, timeout]);
}
